<template>
    <div>

        <div class="d-flex align-items-start">
            <h2>
                <i class="fa fa-comments"></i>
                Mijn berichten
            </h2>
            <div class="ms-auto mt-2">
                <button v-on:click="updateComponentVisability(componentId)" class="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#conversations" aria-expanded="true" aria-controls="conversations">
                    <i class="fa fa-chevron-down" aria-hidden="true"></i>
                </button>
            </div>
        </div>

        <div v-if="loading">
            <div class="loading-spinner"><i class="fa fa-circle-o-notch fa-spin fa-2x fa-fw"></i></div>
        </div>

        <div class="fade-in" :class="{ 'loaded': ! loading }">

            <div class="collapse" :class="{'show': this.componentIsVisable}" id="conversations">

                <div class="d-flex flex-wrap position-relative mt-3 mb-4">
                    <div class="text-decoration-none text-nowrap me-4 flex-wrap d-flex" role="button">
                        <span class="bg-lightyellow text-primary round-btn me-2">
                            <i class="fa fa-plus" aria-hidden="true"></i>
                        </span>
                        <span v-on:click="addConversation()" class="fw-semibold">
                            Nieuw gesprek starten
                        </span>
                    </div>
                    <div class="text-decoration-none text-nowrap me-4 flex-wrap d-flex" role="button">
                        <span class="bg-lightyellow text-primary round-btn me-2">
                            <i class="fa fa-archive" aria-hidden="true"></i>
                        </span>
                        <span v-on:click="showArchivedConversations()" class="fw-semibold">
                            Archief
                        </span>
                    </div>
                    <div class="text-decoration-none text-nowrap me-4 position-relative flex-wrap d-flex">
                        <span class="bg-lightyellow text-primary round-btn me-2">
                            <i class="fa fa-filter" aria-hidden="true"></i>
                        </span>
                        <span v-on:click="toggleFilterDropdown()" class="fw-semibold" role="button">
                            Filter
                        </span>
                        <div v-show="showFilterDropdown" class="position-absolute mt-2 bg-white shadow rounded p-2 show-filter-dropdown">
                            <div v-for="filter in conversationFilters"
                                 :key="filter"
                                 class="dropdown-item py-2 px-3 d-flex align-items-center"
                                 :class="{'bg-light fw-bold': currentFilter === filter}"
                                 @click="applyFilter(filter)"
                                 role="button">
                                <i v-if="currentFilter === filter" class="fa fa-check text-primary me-2"></i>
                                <span v-else class="me-4"></span>
                                {{ getFilterLabel(filter) }}
                            </div>
                        </div>
                    </div>
                    <div class="text-nowrap text-end position-absolute unread-conversation-circle">
                        <span class="bg-lightyellow text-primary fw-semibold round-btn" style="font-size: 1.4em;">
                            {{ this.unreadConversations }}
                        </span>
                    </div>
                </div>

                <div class="mb-4">
                    <!-- Eén zoekveld (volledige breedte) voor dossiernummer, postcode, huisnummer en berichtinhoud -->
                    <div class="mb-4 w-full position-relative">
                        <input
                            type="text"
                            class="w-full form-control conversations-search-input"
                            v-model="searchQuery"
                            placeholder="Zoek op dossier, postcode, huisnummer of berichtinhoud"
                            v-on:keyup.enter="searchConversations()"
                        >
                        <span
                            v-if="searchQuery"
                            v-on:click="clearSearch()"
                            class="position-absolute clear-search-btn"
                            role="button"
                            title="Wissen"
                        >
                            <i class="fa fa-times"></i>
                        </span>
                        <span
                            v-on:click="searchConversations()"
                            class="position-absolute search-icon-btn"
                            role="button"
                            title="Zoeken"
                        >
                            <i class="fa fa-search" :class="{ 'fa fa-circle-o-notch fa-spin' : this.searchLoader }"></i>
                        </span>
                    </div>

                </div>

                <div v-if="showResults" class="mb-3">
                    <label class="form-label fw-semibold">Zoekresultaten</label>
                    <div v-if="this.searchResults.length > 0">
                        <div class="list-group">
                            <a
                                v-for="result in searchResults"
                                :key="result.id"
                                href="#"
                                class="list-group-item list-group-item-action"
                                @click.prevent="showConversation(result)"
                            >
                                <div class="w-full flex gap-2">
                                    <div v-if="result.dossier_nr" v-html="highlight(result.dossier_nr)"></div>
                                    <div v-html="highlight(result.order_name)"></div>
                                    <div class="ms-auto">
                                        <small class="d-block text-muted">{{ result.status }}</small>
                                    </div>
                                </div>
                                <div v-if="result.match_snippet" class="mt-1 text-sm text-lightgrey search-result-snippet">
                                    <i
                                        class="me-1"
                                        :class="result.match_type === 'message' ? 'fa fa-comment-o' : 'fa fa-map-marker'"
                                    ></i>
                                    <span v-html="highlight(result.match_snippet)"></span>
                                </div>
                                <div v-if="result.users_count" class="mt-1 text-sm text-lightgrey search-result-users">
                                    <i class="fa fa-users me-1"></i>
                                    <span>{{ result.users_count }} {{ result.users_count === 1 ? 'deelnemer' : 'deelnemers' }}</span>
                                    <span v-if="result.users && result.users.length"> &middot; {{ result.users.join(', ') }}</span>
                                </div>
                            </a>
                        </div>
                    </div>
                    <div v-else class="mb-3">Geen resultaten gevonden</div>
                </div>

                <div
                    class="messages"
                    v-if="this.conversations.length !== 0"
                >
                    <div
                        v-for="conversation in this.conversations"
                        class="message"
                    >
                        <div class="content"
                             :class="{ hasBeenRead: conversation.viewed_at !== null}"
                             v-on:click="showConversation(conversation)"
                        >

                            <div class="d-flex flex-column-reverse flex-sm-row align-items-baseline">
                                <p class="sender">
                                    {{ conversation.last_message.user }}
                                    <i class="fa fa-paper-plane ms-1"></i>
                                </p>
                                <span class="date ms-sm-auto">
                                    {{ conversation.last_message.created_at }}
                                </span>
                            </div>
                            <div>
                                <p class="mb-0 fw-semibold">
                                    <i v-if="conversation.is_internal" class="fa fa-users text-primary me-2" title="Intern gesprek"></i>
                                    <span v-if="!conversation.is_internal && conversation.dossier_nr">{{ conversation.dossier_nr }} - </span>
                                    {{ conversation.order_name }}
                                </p>
                                <p v-html="conversation.last_message.message_limit" class="text-sm text-lightgrey mb-0"></p>
                            </div>
                        </div>
                        <div class="actions">
                            <span v-if="!conversation.is_internal && conversation.order_url">
                                <a :href="conversation.order_url" target="_blank">
                                    <i class="fa fa-external-link"></i>
                                </a>
                            </span>
                            <div v-else style="width: 1em;">
                                &nbsp;
                            </div>

                            <span v-on:click="hideConversation(conversation)" role="button">
                                <i class="fa fa-eye-slash"></i>
                            </span>
                            <span v-on:click="showAddUsersToConversation(conversation)" role="button">
                                <i class="fa fa-user"></i>
                            </span>
                        </div>
                    </div>
                </div>

                <div v-else>
                    <div class="content">
                        Geen berichten
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
    import {EventBus} from "../../event-bus";
    import {usePolling} from "../../composables/usePolling";
    import {useComponentVisibility} from "../../composables/useComponentVisibility";

    export default {
        name: 'conversations',
        props: {
            isVisible: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                conversations: [{
                    id: '',
                    dossier_nr: '',
                    order_name: '',
                    status: '',
                    order_url: '',
                    updated_at: '',
                    viewed_at: '',
                    done_at: '',
                    last_message: {
                        id: '',
                        user: '',
                        message_limit: '',
                        created_at: '',
                    },
                }],
                previousConversations: [],
                currentUserId: null,
                dismissedPopups: {},

                searchQuery: '',
                searchResults: [],
                showResults: false,
                searchLoader: false,

                unreadConversations: 0,
                componentId: 'conversations-component',
                componentIsVisable: this.isVisible,
                conversationFilters: [
                    'all','not_viewed','viewed'
                ],
                currentFilter: 'all',
                loading: true,
                showFilterDropdown: false,
            }
        },
        computed: {},
        watch: {},
        methods: {
            loadDismissedPopups() {
                try {
                    const raw = localStorage.getItem('opthoog_conversation_popups');
                    this.dismissedPopups = raw ? JSON.parse(raw) : {};
                } catch (e) {
                    this.dismissedPopups = {};
                }
            },
            saveDismissedPopups() {
                try {
                    localStorage.setItem('opthoog_conversation_popups', JSON.stringify(this.dismissedPopups || {}));
                } catch (e) {}
            },
            isPopupDismissed(conversationId, messageId) {
                if (!conversationId || !messageId) return false;
                const key = String(conversationId);
                return !!(this.dismissedPopups[key] && this.dismissedPopups[key][String(messageId)]);
            },
            markPopupDismissed(conversationId, messageId) {
                if (!conversationId || !messageId) return;
                const key = String(conversationId);
                if (!this.dismissedPopups[key]) this.dismissedPopups[key] = {};
                this.dismissedPopups[key][String(messageId)] = true;
                this.saveDismissedPopups();
            },
            updateComponentVisability(component_id) {
                // Toggle the visibility state
                this.componentIsVisable = !this.componentIsVisable;
                axios.get('api/update-component-visability', {
                    params: {
                        component_id: component_id,
                        is_visable: this.componentIsVisable
                    }
                });
            },
            addConversation() {
                EventBus.$emit('openNewConversationModal');
            },
            showArchivedConversations() {
                EventBus.$emit('openArchivedConversationsModal');
            },
            toggleFilterDropdown() {
                this.showFilterDropdown = !this.showFilterDropdown;
            },
            getFilterLabel(filter) {
                const labels = {
                    'all': 'Alle berichten',
                    'not_viewed': 'Ongelezen berichten',
                    'viewed': 'Gelezen berichten'
                };
                return labels[filter] || filter;
            },
            applyFilter(filter) {
                this.currentFilter = filter;
                this.showFilterDropdown = false;
                this.getConversations();
            },
            searchConversations() {
                if (this.searchQuery.trim() === '') {
                    this.searchResults = [];
                    this.showResults = false;
                    return;
                }

                this.searchLoader = true;

                axios.get('api/search-conversations', {
                    params: {
                        query: this.searchQuery
                    }
                })
                .then(response => {
                    if (response.data.status === 422) {
                        throw [true, response.data.message];
                    }
                    this.searchResults = response.data.results || [];
                    this.searchLoader = false;
                    this.showResults = true;
                })
                .catch(e => {
                    this.searchLoader = false;
                    this.$swal("Er is een fout opgetreden", e[0] === true ? e[1] : "Er is iets mis gegaan bij het zoeken.", "error");
                });
            },
            escapeHtml(value) {
                return String(value)
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&#039;');
            },
            escapeRegExp(value) {
                return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            },
            highlight(text) {
                const safe = this.escapeHtml(text ?? '');
                const term = (this.searchQuery || '').trim();

                if (term === '') {
                    return safe;
                }

                const pattern = new RegExp('(' + this.escapeRegExp(this.escapeHtml(term)) + ')', 'gi');
                return safe.replace(pattern, '<mark>$1</mark>');
            },
            getConversations() {
                axios.get('api/get-user-conversations', {
                    params: {
                        filter: this.currentFilter
                    }
                })
                    .then(response => {
                        if (response.data.status === 422) {
                            throw [true, response.data.message];
                        }

                        const items = response.data.conversationItems || [];

                        // Check for new messages by comparing with previous conversations (polling case)
                        if (this.previousConversations.length > 0) {
                            const result = this.checkForNewMessages(items);
                            if (result && result.isNew) {
                                EventBus.$emit('newMessageReceived', result);
                            }
                        }

                        // Store current conversations for future comparison
                        this.previousConversations = JSON.parse(JSON.stringify(items));
                        this.conversations = items;

                        this.unreadConversations = response.data.unreadConversations;
                        this.componentId = response.data.componentId;
                        this.currentUserId = response.data.currentUserId;
                        this.loading = false;

                        // Initial page load: if there are unread messages from others that were not dismissed yet, show once
                        if (!this._initialChecked) {
                            this._initialChecked = true;
                            const initial = this.findUnreadNotDismissed(items);
                            if (initial) {
                                EventBus.$emit('newMessageReceived', initial);
                            }
                        }
                    })
                    .catch(e => {
                        this.$swal("Er is een fout opgetreden", e[0] === true ? e[1] : "Er is iets mis gegaan bij het ophalen van de data", "error");
                    });
            },
            findUnreadNotDismissed(items) {
                if (this.currentFilter === 'viewed') return null;
                if (!items || items.length === 0) return null;
                // items are already ordered by last_message created_at DESC by backend
                for (const conv of items) {
                    if (conv.viewed_at === null && conv.last_message && conv.last_message.user_id !== this.currentUserId) {
                        if (!this.isPopupDismissed(conv.id, conv.last_message.id)) {
                            return {
                                isNew: true,
                                title: conv.order_name,
                                dossierNr: conv.dossier_nr,
                                conversationId: conv.id,
                                messageId: conv.last_message.id,
                            };
                        }
                    }
                }
                return null;
            },
            hideConversation(conversation) {

                let parent = this;

                this.$swal({
                    title: "Gesprek archiveren. Weet je het zeker?",
                    text: "Deze actie kan (momenteel) niet ongedaan gemaakt worden.",
                    showCancelButton: true,
                    cancelButtonColor: "#3EBE00",
                    confirmButtonColor: "#DD3444",
                    confirmButtonText: "Ja, nu archiveren",
                    cancelButtonText: "Annuleren",
                }).then(

                    function (callBack) {
                        if (callBack.isConfirmed) {
                            axios.post('api/hide-conversation-user', {
                                conversationId: conversation.id,
                            })
                            .then(response => {
                                if (response.data.status === 422) {
                                    throw [true, response.data.message];
                                }

                                parent.getConversations();
                            })
                            .catch(e => {
                                this.$swal("Er is een fout opgetreden", e[0] === true ? e[1] : "Er is iets mis gegaan bij het opslaan van de gegevens.", "error");
                            });
                        }
                    }
                );
            },
            showConversation(conversation) {
                axios.get('api/get-conversation-messages/'+conversation.id)
                    .then(response => {
                        if (response.data.status === 422) {
                            throw [true, response.data.message];
                        }

                        this.searchQuery = '';
                        this.searchResults = [];
                        this.showResults = false;
                        EventBus.$emit('conversationSelected', response.data);

                    })
                    .catch(e => {
                        this.$swal("Er is een fout opgetreden", e[0] === true ? e[1] : "Er is iets mis gegaan bij het ophalen van de data", "error");
                    });
            },
            showAddUsersToConversation(conversation) {
                axios.get('api/add-users-to-conversation/'+conversation.id)
                    .then(response => {
                        if (response.data.status === 422) {
                            throw [true, response.data.message];
                        }

                        EventBus.$emit('addUsersToConversation', response.data);

                    })
                    .catch(e => {
                        this.$swal("Er is een fout opgetreden", e[0] === true ? e[1] : "Er is iets mis gegaan bij het ophalen van de data", "error");
                    });
            },
            checkForNewMessages(newConversations) {
                if(this.currentFilter === 'viewed') {
                    return false;
                }

                if (!this.previousConversations || this.previousConversations.length === 0) {
                    return false;
                }

                const prevConversationsMap = {};
                this.previousConversations.forEach(conv => {
                    prevConversationsMap[conv.id] = conv;
                });

                for (const newConv of newConversations) {
                    const prevConv = prevConversationsMap[newConv.id];

                    if (!prevConv) {
                        if (!newConv.last_message || newConv.last_message.user_id === this.currentUserId) {
                            continue; // Skip notification for own messages
                        }
                        if (this.isPopupDismissed(newConv.id, newConv.last_message.id)) {
                            continue;
                        }
                        return {
                            isNew: true,
                            title: newConv.order_name,
                            dossierNr: newConv.dossier_nr,
                            conversationId: newConv.id,
                            messageId: newConv.last_message.id,
                        };
                    }

                    if (newConv.last_message && newConv.last_message.id !== prevConv.last_message.id) {
                        if (newConv.last_message.user_id === this.currentUserId) {
                            continue;
                        }
                        if (this.isPopupDismissed(newConv.id, newConv.last_message.id)) {
                            continue;
                        }
                        return {
                            isNew: true,
                            title: newConv.order_name,
                            dossierNr: newConv.dossier_nr,
                            conversationId: newConv.id,
                            messageId: newConv.last_message.id,
                        };
                    }
                }

                return false;
            },
            handleClickOutside(event) {
                // Get the filter dropdown and filter link elements
                const filterDropdown = document.querySelector('.position-relative');

                // If the dropdown is open and the click is outside the dropdown, close it
                if (this.showFilterDropdown && filterDropdown && !filterDropdown.contains(event.target)) {
                    this.showFilterDropdown = false;
                }
            },
            clearSearch() {
                this.searchQuery = '';
                this.searchResults = [];
                this.showResults = false;
            },
        },
        mounted() {
            this.loadDismissedPopups();
            this._initialChecked = false;

            EventBus.$on('newMessageDismissed', (payload) => {
                if (payload && payload.conversationId && payload.messageId) {
                    this.markPopupDismissed(payload.conversationId, payload.messageId);
                }
            });

            const { startPolling } = usePolling(() => {
                this.getConversations();
            }, 60000);

            startPolling();

            EventBus.$on('refetchConversations', () => {
                this.getConversations();
            });

            // Add click outside handler to close the filter dropdown
            document.addEventListener('click', this.handleClickOutside);
        },
        beforeDestroy() {
            // Remove click outside handler when component is destroyed
            document.removeEventListener('click', this.handleClickOutside);
        },
        created() {},
    }
</script>
<style>
input.form-control {
    &:disabled {
        background: #f7f7f7;
    }
}

.show-filter-dropdown {
    z-index: 1000;
    min-width: 200px;
    margin-left: 0;
    margin-top: 30px !important;
}

.unread-conversation-circle {
    top: 0px;
    right: 0px;
}

.conversations-search-input {
    padding-right: 64px;
}

.clear-search-btn {
    top: 50%;
    right: 40px;
    transform: translateY(-50%);
    color: #aaa;
    line-height: 1;
}

.clear-search-btn:hover {
    color: #666;
}

.search-icon-btn {
    top: 50%;
    right: 14px;
    transform: translateY(-50%);
    color: #888;
    line-height: 1;
}

.search-icon-btn:hover {
    color: #444;
}

.search-result-users {
    line-height: 1.4;
}

.search-result-snippet {
    word-break: break-word;
    line-height: 1.4;
}

.search-result-snippet mark,
.list-group-item mark {
    background-color: #fff3b0;
    padding: 0 1px;
    border-radius: 2px;
}
</style>
